import React, { useState } from "react";
import {
  Stack,
  Box,
  Group,
  Text,
  Button,
  NumberInput,
  ActionIcon,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconPlus, IconTrash, IconAlertCircle } from "@tabler/icons-react";
import {
  CommissionTierDraft,
  CommissionTierDrafts,
  TierBucket,
} from "../../types/commission";
import {
  TIER_BUCKETS,
  TIER_BUCKET_LABELS,
  tierCategoryError,
} from "../../utils/commissionUtils";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import SettingsTabs from "./common/SettingsTabs";
import styles from "./TieredCommissionBuilder.module.css";

const MAX_TIERS = 10;

interface TieredCommissionBuilderProps {
  value: CommissionTierDrafts;
  onChange: (value: CommissionTierDrafts) => void;
  disabled?: boolean;
}

const baseRow = (): CommissionTierDraft => ({ from: 0, rate: "" });

/**
 * Editor for a Step/Tiered commission schedule. A category tab bar (Services /
 * Products / Packages) switches between three independent tier tables. Per
 * category the owner defines up to 10 escalating steps; each step's rate applies
 * to the *entire* category total once earnings reach it (a cliff, not marginal).
 *
 * The owner edits each step's **Starting from** amount and **Rate**; the **Up
 * to** bound is derived (one cent under the next step's start, or "No limit" for
 * the last) so the schedule can never gap. Row 0 is the base step, fixed at $0
 * and never removable — it is the empty state (a lone base step = 0% for that
 * category). Values are held as {@link CommissionTierDrafts}; the parent converts
 * to/from the wire shape via `draftsToWire` / `wireToDrafts`.
 */
const TieredCommissionBuilder: React.FC<TieredCommissionBuilderProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [active, setActive] = useState<TierBucket>("services");
  const isMobile = useMediaQuery("(max-width: 600px)");

  const rows =
    value[active] && value[active]!.length > 0 ? value[active]! : [baseRow()];
  const label = TIER_BUCKET_LABELS[active];

  const setRows = (next: CommissionTierDraft[]) =>
    onChange({ ...value, [active]: next });

  const updateRow = (index: number, patch: Partial<CommissionTierDraft>) =>
    setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const addTier = () => {
    if (rows.length >= MAX_TIERS) return;
    setRows([...rows, { from: "", rate: "" }]);
  };

  const removeTier = (index: number) => {
    if (index === 0) return; // the base $0 step is never removable
    setRows(rows.filter((_, i) => i !== index));
  };

  // Derived "Up to": one cent under the next step's start; unbounded on the last
  // step; an em dash when the next step's start hasn't been filled in yet.
  const upTo = (index: number): string => {
    if (index === rows.length - 1) return "No limit";
    const next = rows[index + 1].from;
    if (next === "" || Number.isNaN(Number(next))) return "—";
    return formatCurrency(Number(next) - 0.01);
  };

  const categoryError = tierCategoryError(rows);

  const tabItems = TIER_BUCKETS.map((bucket) => ({
    value: bucket,
    label: TIER_BUCKET_LABELS[bucket],
  }));

  const renderFrom = (
    row: CommissionTierDraft,
    index: number,
    rowLabel: string,
  ) =>
    index === 0 ? (
      <Box
        className={styles.readonly}
        aria-label={`${rowLabel} starting amount`}
      >
        {formatCurrency(0)}
      </Box>
    ) : (
      <NumberInput
        aria-label={`${rowLabel} starting amount`}
        prefix="$"
        thousandSeparator=","
        placeholder="0"
        min={0}
        decimalScale={2}
        hideControls
        value={row.from === "" ? "" : row.from}
        disabled={disabled}
        onChange={(val) =>
          updateRow(index, { from: val === "" ? "" : Number(val) })
        }
      />
    );

  const renderUpTo = (index: number, rowLabel: string) => (
    <Box className={styles.readonly} aria-label={`${rowLabel} upper limit`}>
      {upTo(index)}
    </Box>
  );

  const renderRate = (
    row: CommissionTierDraft,
    index: number,
    rowLabel: string,
  ) => (
    <NumberInput
      aria-label={`${rowLabel} rate`}
      suffix="%"
      placeholder="0"
      min={0}
      max={100}
      decimalScale={2}
      hideControls
      value={row.rate === "" ? "" : row.rate}
      disabled={disabled}
      onChange={(val) =>
        updateRow(index, { rate: val === "" ? "" : Number(val) })
      }
    />
  );

  const renderDelete = (index: number, rowLabel: string) =>
    index === 0 ? (
      <Box />
    ) : (
      <ActionIcon
        variant="subtle"
        color="red"
        aria-label={`Remove ${rowLabel}`}
        disabled={disabled}
        onClick={() => removeTier(index)}
      >
        <IconTrash size={18} />
      </ActionIcon>
    );

  const renderRow = (row: CommissionTierDraft, index: number) => {
    const rowLabel = `${label} tier ${index + 1}`;
    if (isMobile) {
      return (
        <Box key={index} className={styles.mobileCard} aria-label={rowLabel}>
          <Group justify="space-between" align="center" mb={8}>
            <Text fw={600} size="sm">
              Tier {index + 1}
            </Text>
            {renderDelete(index, rowLabel)}
          </Group>
          <Stack gap={10}>
            <Box>
              <Text className={styles.mobileLabel}>Starting from</Text>
              {renderFrom(row, index, rowLabel)}
            </Box>
            <Box>
              <Text className={styles.mobileLabel}>Up to</Text>
              {renderUpTo(index, rowLabel)}
            </Box>
            <Box>
              <Text className={styles.mobileLabel}>Rate</Text>
              {renderRate(row, index, rowLabel)}
            </Box>
          </Stack>
        </Box>
      );
    }
    return (
      <Box key={index} className={styles.row} aria-label={rowLabel}>
        {renderFrom(row, index, rowLabel)}
        {renderUpTo(index, rowLabel)}
        {renderRate(row, index, rowLabel)}
        {renderDelete(index, rowLabel)}
      </Box>
    );
  };

  return (
    <Stack gap="sm">
      <SettingsTabs
        items={tabItems}
        value={active}
        onChange={(val) => setActive(val as TierBucket)}
        ariaLabel="Commission categories"
      />

      {!isMobile && (
        <Box className={styles.row} aria-hidden>
          <Text className={styles.headCell}>Starting from</Text>
          <Text className={styles.headCell}>Up to</Text>
          <Text className={styles.headCell}>Rate</Text>
          <Box />
        </Box>
      )}

      <Stack gap={isMobile ? "sm" : "xs"}>
        {rows.map((row, index) => renderRow(row, index))}
      </Stack>

      {categoryError && (
        <Group gap={7} align="center">
          <IconAlertCircle size={15} color="var(--settings-alert)" />
          <Text size="xs" c="var(--settings-alert)" fw={500}>
            {categoryError}
          </Text>
        </Group>
      )}

      <Group justify="flex-start">
        <Button
          variant="subtle"
          color="var(--brand-teal)"
          size="xs"
          leftSection={<IconPlus size={14} />}
          disabled={disabled || rows.length >= MAX_TIERS}
          onClick={addTier}
        >
          Add tier
        </Button>
      </Group>
    </Stack>
  );
};

export default TieredCommissionBuilder;
